import React from 'react';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Badge, Box, Button, Chip, type ChipProps, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { GridColDef } from '@mui/x-data-grid';

import {
  type CxTicketRow,
  EMPTY_VALUE,
  INTERNAL_COMMENT_SIGNAL_THRESHOLD,
  LTV_BAND_LABELS,
  type LtvBand,
  REASSIGNMENT_SIGNAL_THRESHOLD,
  REOPEN_SIGNAL_THRESHOLD,
  RISK_TIER_COLORS,
  SEVEN_DAYS_IN_MINUTES,
  SOCIETE_TIER_THEME_KEYS,
  describeTierChange,
  formatAiSignalLabel,
  formatAud,
  formatCompactAud,
  formatCount,
  formatDateTime,
  formatEmptyValue,
  formatHours,
  formatKeywordList,
  formatMinutesAsDuration,
  formatPercentile,
  formatShortDateTime,
  getAiSignalTokens,
  isEmergingSignal,
  isSocieteTier,
  joinDetails,
  toJourneyPhaseLabel,
  toJourneySortKey,
  toJourneyTooltip,
  zendeskTicketUrl,
} from './ticketRiskRows';

export interface CxColumnGroup {
  id: string;
  label: string;
  columns: Array<GridColDef<CxTicketRow>>;
}

interface CxColumnOptions {
  /** Opens the detail dialog — the home of every field the triage columns compress away. */
  onOpenTicket: (ticketId: string) => void;
  onWatchTicket: (ticketId: string) => void;
  isWatchPending: boolean;
}

/** The compressed default view. Every other group is opt-in through the column picker. */
export const TRIAGE_GROUP_ID = 'triage';

type TicketStatusStyle = Pick<ChipProps, 'color' | 'sx'>;

const getTicketStatusStyle = (status: string): TicketStatusStyle => {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'new' || normalizedStatus === 'open') {
    return { color: 'warning' };
  }

  if (normalizedStatus === 'solved' || normalizedStatus === 'closed') {
    return {
      color: 'default',
      sx: (theme) => ({
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.common.white,
      }),
    };
  }

  if (normalizedStatus.includes('awaiting')) {
    return { color: 'error' };
  }

  if (normalizedStatus === 'pending internal review' || normalizedStatus === 'pending partner response') {
    return {
      color: 'default',
      sx: (theme) => ({
        backgroundColor: theme.palette.text.primary,
        color: theme.palette.background.paper,
      }),
    };
  }

  if (normalizedStatus === 'pending customer response') {
    return { color: 'primary' };
  }

  return { color: 'default' };
};

/**
 * The compressed columns stack a headline value over its supporting detail, so fields that are
 * always read together share one column instead of one each.
 */
function StackedCell({ primary, secondary }: { primary: React.ReactNode; secondary?: React.ReactNode }) {
  return (
    <Stack justifyContent="center" spacing={0.25} sx={{ height: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        {primary}
      </Stack>
      {secondary != null && (
        <Typography variant="caption" color="text.secondary" component="div" noWrap sx={{ minWidth: 0 }}>
          {secondary}
        </Typography>
      )}
    </Stack>
  );
}

interface TicketSignal {
  id: string;
  /** Short enough for two chips to a line at the column's width. */
  label: string;
  isPresent: (row: CxTicketRow) => boolean;
  /** The full sentence, with the actual number, for the chip's tooltip. */
  describe: (row: CxTicketRow) => string;
  getColor: (row: CxTicketRow) => ChipProps['color'];
}

const isComplaint = (row: CxTicketRow) => row.ticketReason?.trim().toLowerCase() === 'complaint';

/**
 * The row-level twin of the "Signals to Watch" filter bar: the same thresholds, drawn per ticket.
 * Each of these was its own column, but they are threshold flags rather than numbers you compare —
 * the raw counts stay one click away in the Activity and Risk column groups.
 *
 * Ordered most to least severe: only the leading few are drawn, so the order decides what a
 * glance at the column tells you.
 */
const TICKET_SIGNALS: Array<TicketSignal> = [
  {
    id: 'legalHold',
    label: 'Legal hold',
    isPresent: (row) => row.legalHold,
    describe: () => 'Legal hold — forced to Critical',
    getColor: () => 'error',
  },
  {
    id: 'authority',
    label: 'Authority',
    isPresent: (row) => row.authorityKeywords.length > 0,
    describe: (row) => `Authority mentioned: ${formatKeywordList(row.authorityKeywords)}`,
    getColor: (row) => (row.authorityConfirmed ? 'error' : 'warning'),
  },
  {
    id: 'negative',
    label: 'Negative',
    isPresent: (row) => row.sentimentBucket === 'Negative',
    describe: (row) => `Negative sentiment${row.sentiment ? `: ${row.sentiment}` : ''}`,
    getColor: () => 'error',
  },
  {
    id: 'customerWaiting',
    label: 'Waiting 7d+',
    isPresent: (row) => row.requesterWaitMinutes >= SEVEN_DAYS_IN_MINUTES,
    describe: (row) => `Customer waiting ${formatMinutesAsDuration(row.requesterWaitMinutes)}`,
    getColor: () => 'error',
  },
  {
    id: 'complaint',
    label: 'Complaint',
    isPresent: isComplaint,
    describe: () => 'Logged as a complaint',
    getColor: () => 'warning',
  },
  {
    id: 'divergent',
    label: 'Divergent',
    isPresent: (row) => row.divergent,
    describe: () => 'Divergent — the language reads as risky but the deterministic facts do not',
    getColor: () => 'warning',
  },
  {
    id: 'reassigned',
    label: 'Reassigned',
    isPresent: (row) => row.reassignCount >= REASSIGNMENT_SIGNAL_THRESHOLD,
    describe: (row) => `Reassigned ${formatCount(row.reassignCount, 'time')}`,
    getColor: () => 'warning',
  },
  {
    id: 'reopened',
    label: 'Reopened',
    isPresent: (row) => row.reopenCount >= REOPEN_SIGNAL_THRESHOLD,
    describe: (row) => `Reopened ${formatCount(row.reopenCount, 'time')}`,
    getColor: () => 'warning',
  },
  {
    id: 'internalComments',
    label: 'Internal 10+',
    isPresent: (row) => row.internalComments >= INTERNAL_COMMENT_SIGNAL_THRESHOLD,
    describe: (row) => `${formatCount(row.internalComments, 'internal comment')} on the ticket`,
    getColor: () => 'default',
  },
];

const countSignals = (row: CxTicketRow) => TICKET_SIGNALS.filter((signal) => signal.isPresent(row)).length;

/**
 * A one-hue intensity ramp rather than the red/amber scale: LTV is magnitude, not risk, and the
 * risk tier chip is already the red thing in the row. An unrecognised band falls through to the
 * same neutral outline as Standard.
 */
const LTV_BAND_CHIP_PROPS: Record<LtvBand, Pick<ChipProps, 'variant' | 'color' | 'sx'>> = {
  vip: {
    variant: 'filled',
    color: 'primary',
    sx: (chipTheme) => ({
      backgroundColor: chipTheme.palette.primary.dark,
      fontWeight: chipTheme.typography.fontWeightBold,
    }),
  },
  top_tier: { variant: 'filled', color: 'primary' },
  valuable: { variant: 'outlined', color: 'primary' },
  standard: { variant: 'outlined', color: 'default' },
};

const NEUTRAL_LTV_CHIP_PROPS: Pick<ChipProps, 'variant' | 'color'> = { variant: 'outlined', color: 'default' };

/** The membership tag borrows the LuxPlus brand colours rather than a generic palette colour. */
const LUX_PLUS_CHIP_SX = (chipTheme: Theme) => ({
  backgroundColor: chipTheme.palette.luxPlus.main,
  color: chipTheme.palette.luxPlus.contrastText,
  fontWeight: chipTheme.typography.fontWeightMedium,
});

/** Two chips a line at the column's width, so the fourth signal onwards collapses into a +N. */
/**
 * Three short rule chips fit the cell's first line at this width. The model's names go on the
 * second line as text, so the two kinds never compete for the same space — as chips they took
 * a third line and broke out of the fixed row height.
 */
const MAX_NAMED_SIGNALS = 3;

/**
 * AI names that restate a rule chip already drawn on the same row, suppressed so the cell does
 * not say the same thing twice from two pipelines. Conservative on purpose: an unlisted synonym
 * costs a duplicate chip, which is untidy, where over-listing would hide a signal the model
 * raised on its own. svc-support's vocabulary is unstable enough that this will need topping up
 * — the fix is for it to mark which names restate a fact the report already carries.
 */
const AI_SIGNALS_RESTATING_RULE_CHIPS: Record<string, ReadonlyArray<string>> = {
  negative: ['negative_sentiment', 'very_negative_sentiment', 'negative_feedback'],
  reassigned: ['high_reassign_count', 'multiple_reassignments'],
  reopened: ['high_reopen_count', 'multiple_reopens', 'ticket_reopened', 'ticket_reopens', 'reopens'],
  customerWaiting: ['customer_waiting', 'customer_wait_time', 'requester_wait_minutes', 'long_wait', 'long_wait_time'],
  complaint: ['formal_complaint'],
  authority: ['authority_threat'],
  legalHold: ['legal_ownership'],
};

/** The model's names for this row, minus the ones a rule chip beside them already says. */
const toAdditiveAiSignals = (row: CxTicketRow, presentRuleSignalIds: Array<string>): Array<string> => {
  const restated = new Set(presentRuleSignalIds.flatMap((id) => AI_SIGNALS_RESTATING_RULE_CHIPS[id] ?? []));

  return getAiSignalTokens(row).filter((token) => !restated.has(token));
};

export function buildCxTicketColumnGroups(options: CxColumnOptions): Array<CxColumnGroup> {
  const { onOpenTicket, onWatchTicket, isWatchPending } = options;

  const ticketIdLink = (ticketId: string) => (
    <Button
      component="a"
      href={zendeskTicketUrl(ticketId)}
      target="_blank"
      rel="noreferrer"
      size="small"
      sx={{ minWidth: 0, px: 0 }}
    >
      {ticketId}
    </Button>
  );

  const watchingCell = (row: CxTicketRow) => {
    const { watcher, isWatchedByMe, watcherName, id, commentCount, recentCommentCount, lastComment } = row;
    const commentsLabel = joinDetails([
      commentCount === 0 ? 'No comments yet' : formatCount(commentCount, 'comment'),
      recentCommentCount > 0 ? `${recentCommentCount} in the last hour` : null,
      lastComment ? `last by ${lastComment.authorName}, ${formatShortDateTime(lastComment.createdAt)}` : null,
    ]);
    // The eye takes ownership: watch an unwatched ticket, or take over someone else's. Yours is
    // filled and inert — stopping is a deliberate action, kept in the dialog.
    const eyeLabel = isWatchedByMe
      ? "You're watching this ticket"
      : watcher
        ? `Take over from ${watcherName}`
        : 'Watch this ticket';

    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%', minWidth: 0, maxWidth: '100%' }}>
        <Tooltip title={eyeLabel}>
          <span>
            <IconButton
              size="small"
              color={isWatchedByMe ? 'primary' : 'default'}
              aria-label={eyeLabel}
              disabled={isWatchedByMe || isWatchPending}
              onClick={(event) => {
                event.stopPropagation();
                onWatchTicket(id);
              }}
              sx={{ '&.Mui-disabled': { color: isWatchedByMe ? 'primary.main' : undefined } }}
            >
              {isWatchedByMe ? <VisibilityIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {/* Comments ride along here rather than taking a column. The badge is only what's new (last hour); the tooltip has the total and who spoke last. */}
        <Tooltip title={commentsLabel}>
          <IconButton
            size="small"
            aria-label={commentsLabel}
            onClick={(event) => {
              event.stopPropagation();
              onOpenTicket(id);
            }}
          >
            <Badge badgeContent={recentCommentCount} color="primary" max={99} invisible={recentCommentCount === 0}>
              <ChatBubbleOutlineIcon fontSize="small" color={commentCount > 0 ? 'primary' : 'disabled'} />
            </Badge>
          </IconButton>
        </Tooltip>
        {watcher && (
          <Tooltip title={`Watching since ${formatShortDateTime(watcher.createdAt)}`}>
            <Chip
              size="small"
              color="primary"
              variant={isWatchedByMe ? 'filled' : 'outlined'}
              label={watcherName}
              // Shrinkable, so a long name ellipsizes inside the cell instead of clipping.
              sx={{ minWidth: 0 }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const triageColumns: Array<GridColDef<CxTicketRow>> = [
    {
      field: 'ticket',
      headerName: 'Ticket',
      // Narrow, because the status chip sits under the id rather than beside it. The subject is
      // an opt-in column: Zendesk auto-titles enough tickets that it earned little at this width.
      minWidth: 150,
      flex: 0.9,
      valueGetter: (_value, row) => row.id,
      renderCell: ({ row }) => (
        <StackedCell
          primary={ticketIdLink(row.id)}
          secondary={
            <Chip label={formatEmptyValue(row.ticketStatus)} size="small" {...getTicketStatusStyle(row.ticketStatus)} />
          }
        />
      ),
    },
    {
      field: 'risk',
      headerName: 'Risk',
      minWidth: 165,
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_value, row) => row.riskScore,
      renderCell: ({ row }) => (
        <StackedCell
          primary={
            <>
              <Chip label={row.riskTier} size="small" color={RISK_TIER_COLORS[row.riskTier]} />
              <Typography variant="body2" fontWeight={700}>
                {row.riskScore}
              </Typography>
              {row.isNew && (
                <Tooltip title={describeTierChange(row) ?? ''}>
                  <Chip label="New" size="small" color="secondary" variant="outlined" />
                </Tooltip>
              )}
            </>
          }
          secondary={
            <Box component="span" sx={{ color: row.llmTrajectory === 'deteriorating' ? 'error.main' : 'inherit' }}>
              {joinDetails([row.llmTrajectory, row.percentile == null ? null : formatPercentile(row.percentile)])}
            </Box>
          }
        />
      ),
    },
    {
      field: 'signals',
      headerName: 'Signals',
      minWidth: 320,
      flex: 2,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      description:
        'The flags behind the score: outlined chips are the Signals to Watch thresholds, filled ones are the names the scorer gave the conversation.',
      // Both kinds count, so sorting ranks how much was flagged rather than only the rules.
      valueGetter: (_value, row) =>
        countSignals(row) +
        toAdditiveAiSignals(
          row,
          TICKET_SIGNALS.filter((signal) => signal.isPresent(row)).map((signal) => signal.id),
        ).length,
      renderCell: ({ row }) => {
        const present = TICKET_SIGNALS.filter((signal) => signal.isPresent(row));
        const aiSignals = toAdditiveAiSignals(
          row,
          present.map((signal) => signal.id),
        );

        if (present.length === 0 && aiSignals.length === 0) {
          return EMPTY_VALUE;
        }

        // Named rather than drawn: an icon strip read as decoration and made every signal a
        // hover. The rules stay chips because they are the thresholds the filters use; the
        // model's names read as one truncating line, which is what keeps the row two lines tall.
        const named = present.slice(0, MAX_NAMED_SIGNALS);
        const rest = present.slice(MAX_NAMED_SIGNALS);
        // Spelled out here as in the detail dialog — colour carried it while these were chips,
        // and the word survives truncation where a hue does not.
        const aiLabel = aiSignals
          .map((token) => `${formatAiSignalLabel(token)}${isEmergingSignal(token) ? ' (emerging)' : ''}`)
          .join(' · ');

        return (
          <StackedCell
            primary={
              <>
                {named.map(({ id, label, describe, getColor }) => (
                  <Tooltip key={id} title={describe(row)}>
                    <Chip label={label} size="small" variant="outlined" color={getColor(row)} />
                  </Tooltip>
                ))}
                {rest.length > 0 && (
                  <Tooltip title={rest.map((signal) => signal.describe(row)).join(' · ')}>
                    <Chip label={`+${rest.length}`} size="small" variant="outlined" />
                  </Tooltip>
                )}
              </>
            }
            secondary={
              aiLabel ? (
                <Tooltip title={`Named by the scorer: ${aiLabel}`}>
                  <Box component="span">{aiLabel}</Box>
                </Tooltip>
              ) : undefined
            }
          />
        );
      },
    },
    {
      field: 'watcherName',
      headerName: 'Watching',
      minWidth: 210,
      flex: 1.2,
      renderCell: ({ row }) => watchingCell(row),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      minWidth: 200,
      flex: 1.4,
      valueGetter: (_value, row) => row.ticketReason ?? '',
      renderCell: ({ row }) => (
        <Tooltip title={joinDetails([row.ticketReason, row.subcategory, row.triageCategory])}>
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <StackedCell
              primary={
                <>
                  <Typography variant="body2" noWrap>
                    {formatEmptyValue(row.ticketReason)}
                  </Typography>
                  {row.priority && <Chip label={row.priority} size="small" variant="outlined" />}
                </>
              }
              secondary={formatEmptyValue(row.subcategory)}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'waiting',
      headerName: 'Waiting',
      minWidth: 175,
      flex: 1.1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      description: 'How long the customer has been waiting since their latest message with no agent reply.',
      valueGetter: (_value, row) => row.requesterWaitMinutes,
      renderCell: ({ row }) => (
        <Tooltip
          title={joinDetails([
            `Customer waiting ${formatMinutesAsDuration(row.requesterWaitMinutes)}`,
            `awaiting review ${formatHours(row.hoursAwaitingReview)}`,
            `since last contact ${formatHours(row.hoursSinceLastContact)}`,
            `last agent reply ${formatDateTime(row.lastAgentReplyAt)}`,
          ])}
        >
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <StackedCell
              primary={
                <Typography
                  variant="body2"
                  noWrap
                  fontWeight={row.requesterWaitMinutes >= SEVEN_DAYS_IN_MINUTES ? 700 : 400}
                  color={row.requesterWaitMinutes >= SEVEN_DAYS_IN_MINUTES ? 'error.main' : 'text.primary'}
                >
                  {formatMinutesAsDuration(row.requesterWaitMinutes)}
                </Typography>
              }
              secondary={`Review ${formatHours(row.hoursAwaitingReview)} · Contact ${formatHours(row.hoursSinceLastContact)}`}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'age',
      headerName: 'Age',
      minWidth: 140,
      flex: 0.9,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_value, row) => row.caseAgeDays,
      renderCell: ({ row }) => (
        <Tooltip
          title={joinDetails([`Created ${formatDateTime(row.created)}`, `updated ${formatDateTime(row.lastUpdated)}`])}
        >
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <StackedCell
              primary={
                <Typography variant="body2" noWrap>
                  {formatCount(row.caseAgeDays, 'day')}
                </Typography>
              }
              secondary={`Updated ${formatShortDateTime(row.lastUpdated)}`}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'ownership',
      headerName: 'Ownership',
      minWidth: 200,
      flex: 1.2,
      // The agent leads, so the column sorts by who is holding the ticket rather than by a group
      // name that barely varies across the queue.
      valueGetter: (_value, row) => row.assignee ?? '',
      renderCell: ({ row }) => (
        // "TL" prefixes the leader so the two names on the second line stay tellable apart; the
        // tooltip spells all three roles out.
        <Tooltip
          title={joinDetails([
            `Assignee ${formatEmptyValue(row.assignee)}`,
            `group ${formatEmptyValue(row.team)}`,
            `team leader ${formatEmptyValue(row.teamLeader)}`,
          ])}
        >
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <StackedCell
              primary={
                <Typography variant="body2" noWrap>
                  {formatEmptyValue(row.assignee)}
                </Typography>
              }
              secondary={joinDetails([row.team, row.teamLeader ? `TL ${row.teamLeader}` : null])}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      minWidth: 200,
      flex: 1.2,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      // Sorted by journey rather than value: an in-stay guest is the urgent one, whatever they are worth.
      valueGetter: (_value, row) => toJourneySortKey(row),
      renderCell: ({ row }) => {
        const tier = row.societeTier;

        const ltvChipProps = row.ltvBandKey ? LTV_BAND_CHIP_PROPS[row.ltvBandKey] : NEUTRAL_LTV_CHIP_PROPS;

        return (
          <StackedCell
            // Journey and value lead: they are what decides whether a ticket is handled now.
            primary={
              <>
                <Tooltip title={toJourneyTooltip(row)}>
                  <Chip
                    label={toJourneyPhaseLabel(row)}
                    size="small"
                    color={row.journeyPhase === 'In-stay' ? 'primary' : 'default'}
                    variant={row.journeyPhase === 'In-stay' ? 'filled' : 'outlined'}
                  />
                </Tooltip>
                {row.customerLifetimeValue != null && (
                  <Tooltip
                    title={joinDetails([
                      `Lifetime value ${formatAud(row.customerLifetimeValue)}`,
                      row.ltvBandKey ? LTV_BAND_LABELS[row.ltvBandKey] : row.ltvBand,
                    ])}
                  >
                    {/* Never shrinks: the band colour is only readable if the chip is drawn in full. */}
                    <Chip
                      label={formatCompactAud(row.customerLifetimeValue)}
                      size="small"
                      sx={{ flexShrink: 0 }}
                      {...ltvChipProps}
                    />
                  </Tooltip>
                )}
              </>
            }
            secondary={
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                {tier && isSocieteTier(tier) ? (
                  <Chip
                    label={tier}
                    size="small"
                    sx={(chipTheme) => ({
                      backgroundColor: chipTheme.palette.loyaltyTiers[SOCIETE_TIER_THEME_KEYS[tier]].main,
                      color: chipTheme.palette.loyaltyTiers[SOCIETE_TIER_THEME_KEYS[tier]].contrastText,
                      fontWeight: chipTheme.typography.fontWeightMedium,
                    })}
                  />
                ) : null}
                {row.luxPlusMember && <Chip label="Lux+" size="small" sx={LUX_PLUS_CHIP_SX} />}
                <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {tier && !isSocieteTier(tier) ? tier : null}
                </Box>
              </Stack>
            }
          />
        );
      },
    },
  ];

  const ticketColumns: Array<GridColDef<CxTicketRow>> = [
    {
      field: 'id',
      headerName: 'Ticket ID',
      minWidth: 110,
      flex: 0.8,
      renderCell: ({ row }) => ticketIdLink(row.id),
    },
    {
      field: 'subject',
      headerName: 'Subject',
      minWidth: 240,
      flex: 1.6,
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'ticketStatus',
      headerName: 'Ticket Status',
      minWidth: 190,
      flex: 1.4,
      renderCell: (params) => (
        <Chip label={formatEmptyValue(params.value)} size="small" {...getTicketStatusStyle(String(params.value))} />
      ),
    },
    {
      field: 'ticketReason',
      headerName: 'Ticket Reason',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'subcategory',
      headerName: 'Subcategory',
      minWidth: 220,
      flex: 1.5,
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'triageCategory',
      headerName: 'Triage Category',
      minWidth: 160,
      flex: 1,
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      minWidth: 100,
      flex: 0.7,
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'created',
      headerName: 'Created',
      minWidth: 145,
      flex: 1,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      minWidth: 145,
      flex: 1,
      renderCell: (params) => formatDateTime(params.value),
    },
  ];

  const ownershipColumns: Array<GridColDef<CxTicketRow>> = [
    {
      field: 'team',
      headerName: 'Team',
      minWidth: 135,
      flex: 0.9,
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'assignee',
      headerName: 'Assignee',
      minWidth: 160,
      flex: 1.1,
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'teamLeader',
      headerName: 'Team Leader',
      minWidth: 160,
      flex: 1.1,
      renderCell: (params) => formatEmptyValue(params.value),
    },
  ];

  const riskColumns: Array<GridColDef<CxTicketRow>> = [
    {
      field: 'riskTier',
      headerName: 'Risk Tier',
      minWidth: 120,
      flex: 0.8,
      renderCell: (params) => <Chip label={params.value} size="small" color={RISK_TIER_COLORS[params.row.riskTier]} />,
    },
    { field: 'riskScore', headerName: 'Risk Score', minWidth: 95, flex: 0.7, type: 'number' },
    {
      field: 'aiSignals',
      headerName: 'AI Signals',
      minWidth: 220,
      flex: 1.5,
      // Sorted by how many the scorer named, like the Signals column beside it.
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      description: 'The language patterns the scorer named, beyond the deterministic facts in the other columns.',
      valueGetter: (_value, row) => getAiSignalTokens(row).length,
      renderCell: ({ row }) => {
        const tokens = getAiSignalTokens(row);

        if (tokens.length === 0) {
          return EMPTY_VALUE;
        }

        const named = tokens.slice(0, MAX_NAMED_SIGNALS);
        const rest = tokens.slice(MAX_NAMED_SIGNALS);

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignContent: 'center', height: '100%', py: 0.5 }}>
            {named.map((token) => (
              // Colour carries "emerging" here; the detail dialog spells it out instead, where
              // the signals share one line and there is room for the word.
              <Tooltip
                key={token}
                title={isEmergingSignal(token) ? 'Emerging — a pattern outside the scorer&apos;s vocabulary' : token}
              >
                <Chip
                  label={formatAiSignalLabel(token)}
                  size="small"
                  variant="outlined"
                  color={isEmergingSignal(token) ? 'info' : 'secondary'}
                />
              </Tooltip>
            ))}
            {rest.length > 0 && (
              <Tooltip title={rest.map(formatAiSignalLabel).join(' · ')}>
                <Chip label={`+${rest.length}`} size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'llmTrajectory',
      headerName: 'Trajectory',
      minWidth: 120,
      flex: 0.9,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            color={params.value === 'deteriorating' ? 'error' : params.value === 'improving' ? 'success' : 'default'}
          />
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      field: 'divergent',
      headerName: 'Divergent',
      minWidth: 100,
      flex: 0.7,
      type: 'boolean',
      description: 'The language reads as risky but the deterministic facts do not — worth a human look.',
      renderCell: (params) => (params.value ? <Chip label="Yes" size="small" color="warning" /> : EMPTY_VALUE),
    },
    {
      field: 'sentiment',
      headerName: 'Sentiment',
      minWidth: 110,
      flex: 0.8,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            color={params.row.sentimentBucket === 'Negative' ? 'error' : 'default'}
          />
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      field: 'authorityKeywords',
      headerName: 'Authority Mention',
      minWidth: 175,
      flex: 1.2,
      sortable: false,
      description: 'Regulator, chargeback or legal keywords found in the public conversation.',
      renderCell: (params) => {
        const keywords = params.value as Array<string>;

        if (keywords.length === 0) {
          return EMPTY_VALUE;
        }

        return (
          <Chip
            label={formatKeywordList(keywords)}
            size="small"
            color={params.row.authorityConfirmed ? 'error' : 'warning'}
            variant={params.row.authorityConfirmed ? 'filled' : 'outlined'}
          />
        );
      },
    },
  ];

  const scoreBreakdownColumns: Array<GridColDef<CxTicketRow>> = [
    { field: 'hardScore', headerName: 'Hard Score', minWidth: 110, flex: 0.8, type: 'number' },
    {
      field: 'llmScore',
      headerName: 'LLM Score',
      minWidth: 100,
      flex: 0.7,
      type: 'number',
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'percentile',
      headerName: 'Percentile',
      minWidth: 100,
      flex: 0.8,
      type: 'number',
      renderCell: (params) => formatPercentile(params.value),
    },
    {
      field: 'criticalRank',
      headerName: 'Critical Rank',
      minWidth: 110,
      flex: 0.8,
      type: 'number',
      renderCell: (params) => formatEmptyValue(params.value),
    },
    {
      field: 'classificationScore',
      headerName: 'Classification Risk (30)',
      minWidth: 165,
      flex: 1,
      type: 'number',
      description: 'Ticket reason, subcategory or classification tag, scored on measured low-CSAT lift.',
    },
    {
      field: 'riskFlagScore',
      headerName: 'Risk Flags (30)',
      minWidth: 130,
      flex: 0.9,
      type: 'number',
      description: 'Zendesk-detected risk and journey flags on the ticket.',
    },
    {
      field: 'keywordSignalScore',
      headerName: 'Keyword Signals (30)',
      minWidth: 155,
      flex: 1,
      type: 'number',
      description: 'Authority, escalation, health & safety, misrepresentation and review-threat language.',
    },
    { field: 'operationalRiskScore', headerName: 'Operational (20)', minWidth: 140, flex: 0.9, type: 'number' },
    {
      field: 'customerRiskScore',
      headerName: 'Customer Risk (15)',
      minWidth: 145,
      flex: 0.9,
      type: 'number',
      description: 'Stakes from the customer context: in-stay, check-in, tier, Lux+ and LTV.',
    },
  ];

  const timingColumns: Array<GridColDef<CxTicketRow>> = [
    {
      field: 'caseAgeDays',
      headerName: 'Case Age',
      minWidth: 90,
      flex: 0.7,
      type: 'number',
      renderCell: (params) => formatCount(params.value, 'day'),
    },
    {
      field: 'hoursAwaitingReview',
      headerName: 'Time Awaiting Review',
      minWidth: 150,
      flex: 1,
      type: 'number',
      renderCell: (params) => formatHours(params.value),
    },
    {
      field: 'requesterWaitMinutes',
      headerName: 'Customer Waiting',
      minWidth: 140,
      flex: 1,
      type: 'number',
      description: 'How long the customer has been waiting since their latest message with no agent reply.',
      renderCell: (params) => formatMinutesAsDuration(params.value),
    },
    {
      field: 'lastAgentReplyAt',
      headerName: 'Last Agent Reply',
      minWidth: 145,
      flex: 1,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'hoursSinceLastContact',
      headerName: 'Since Last Contact',
      minWidth: 140,
      flex: 1,
      type: 'number',
      renderCell: (params) => formatHours(params.value),
    },
  ];

  const activityColumns: Array<GridColDef<CxTicketRow>> = [
    { field: 'replies', headerName: 'Public Replies', minWidth: 115, flex: 0.8, type: 'number' },
    { field: 'internalComments', headerName: 'Internal Comments', minWidth: 130, flex: 0.9, type: 'number' },
    { field: 'agentsTouched', headerName: 'Agents Touched', minWidth: 125, flex: 0.9, type: 'number' },
    { field: 'reassignCount', headerName: 'Reassigns', minWidth: 100, flex: 0.7, type: 'number' },
    {
      field: 'reopenCount',
      headerName: 'Reopens',
      minWidth: 110,
      flex: 0.8,
      type: 'number',
      renderCell: (params) => formatCount(params.value, 'time'),
    },
  ];

  const customerColumns: Array<GridColDef<CxTicketRow>> = [
    {
      field: 'journeyPhase',
      headerName: 'Journey',
      minWidth: 165,
      flex: 1.1,
      valueGetter: (_value, row) => toJourneySortKey(row),
      renderCell: (params) => (
        <Tooltip title={toJourneyTooltip(params.row)}>
          <Chip
            label={toJourneyPhaseLabel(params.row)}
            size="small"
            color={params.row.journeyPhase === 'In-stay' ? 'primary' : 'default'}
            variant={params.row.journeyPhase === 'In-stay' ? 'filled' : 'outlined'}
          />
        </Tooltip>
      ),
    },
    {
      field: 'societeTier',
      headerName: 'Société Tier',
      minWidth: 105,
      flex: 0.8,
      renderCell: (params) => {
        const tier = params.value as string | null;

        if (!tier) {
          return EMPTY_VALUE;
        }

        if (!isSocieteTier(tier)) {
          return <Chip label={tier} size="small" variant="outlined" />;
        }

        const themeTier = SOCIETE_TIER_THEME_KEYS[tier];

        return (
          <Chip
            label={tier}
            size="small"
            sx={(chipTheme) => ({
              backgroundColor: chipTheme.palette.loyaltyTiers[themeTier].main,
              color: chipTheme.palette.loyaltyTiers[themeTier].contrastText,
              fontWeight: chipTheme.typography.fontWeightMedium,
            })}
          />
        );
      },
    },
    {
      field: 'luxPlusMember',
      headerName: 'Lux+ Member',
      minWidth: 105,
      flex: 0.8,
      type: 'boolean',
      renderCell: (params) => (params.value ? <Chip label="Lux+" size="small" sx={LUX_PLUS_CHIP_SX} /> : EMPTY_VALUE),
    },
    {
      field: 'customerLifetimeValue',
      headerName: 'Customer LTV',
      minWidth: 110,
      flex: 0.9,
      type: 'number',
      renderCell: (params) => formatAud(params.value),
    },
    {
      field: 'ltvBand',
      headerName: 'LTV Band',
      minWidth: 150,
      flex: 1,
      renderCell: ({ row }) =>
        row.ltvBandKey ? (
          <Chip label={LTV_BAND_LABELS[row.ltvBandKey]} size="small" {...LTV_BAND_CHIP_PROPS[row.ltvBandKey]} />
        ) : (
          formatEmptyValue(row.ltvBand)
        ),
    },
  ];

  return [
    { id: TRIAGE_GROUP_ID, label: 'Triage', columns: triageColumns },
    { id: 'ticket', label: 'Ticket', columns: ticketColumns },
    { id: 'ownership', label: 'Ownership', columns: ownershipColumns },
    { id: 'risk', label: 'Risk', columns: riskColumns },
    { id: 'scoreBreakdown', label: 'Score breakdown', columns: scoreBreakdownColumns },
    { id: 'timing', label: 'Timing', columns: timingColumns },
    { id: 'activity', label: 'Activity', columns: activityColumns },
    { id: 'customer', label: 'Customer', columns: customerColumns },
  ];
}

/**
 * Everything outside the triage group starts hidden — the detail dialog already has it all.
 * Derived from the same builder the grid uses, so a column added to a group cannot miss this.
 */
export const CX_DEFAULT_HIDDEN_COLUMN_FIELDS: Array<string> = buildCxTicketColumnGroups({
  onOpenTicket: () => undefined,
  onWatchTicket: () => undefined,
  isWatchPending: false,
})
  .filter((group) => group.id !== TRIAGE_GROUP_ID)
  .flatMap((group) => group.columns.map((column) => column.field));
